/* Reset-password page — landed on via the emailed link (/reset-password/:token).
   Collects a new password + confirmation, hits POST /api/auth/reset-password,
   which also logs the user in on success (server sets a fresh session cookie
   and returns the user object). */
import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import SEO from "@/components/SEO";

const C = {
  bgDark: "#000",
  panel: "#0A2826",
  teal: "#0A8B85",
  tealHover: "#066667",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
};

const ClinqLogo = () => (
  <div className="flex items-center gap-3" data-testid="brand-logo-reset">
    <span className="relative inline-flex items-baseline font-extrabold tracking-tight text-2xl"
      style={{ fontFamily: "Manrope, sans-serif", letterSpacing: "-0.03em", color: C.teal }}>
      Cl
      <span className="relative inline-block">
        ı
        <span aria-hidden="true" className="absolute left-1/2 -translate-x-1/2 rounded-full clinq-dot-glow"
          style={{ backgroundColor: C.orange, height: "0.5rem", width: "0.5rem", bottom: "1.05em" }}></span>
      </span>
      nq
    </span>
    <span className="flex flex-col leading-none text-[10px] uppercase tracking-[0.22em] font-semibold"
      style={{ color: "rgba(255,255,255,0.55)" }}>
      <span>Funder</span>
      <span className="mt-0.5">Readiness</span>
    </span>
  </div>
);

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (pw !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token: token || "",
        new_password: pw,
      });
      // Server logs us in on success (session cookie + user payload).
      if (data?.user) {
        setUser(data.user);
      }
      toast.success("Password updated — you're logged in.");
      navigate("/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not reset password. Try requesting a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-white"
      style={{ backgroundColor: C.bgDark, fontFamily: "Manrope, sans-serif" }}
      data-testid="reset-password-page">
      <SEO title="Set a new password" path="/reset-password" noindex />
      <header style={{ backgroundColor: "#000" }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/"><ClinqLogo /></Link>
          <Link to="/login" data-testid="header-login"
            className="text-sm font-medium text-white/90 hover:text-white px-3 py-2">
            Log In
          </Link>
        </div>
      </header>
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] font-semibold text-white/50">
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: C.orange }}></span>
              Set a new password
            </div>
            <h1 className="font-extrabold text-4xl mt-3 tracking-tight" style={{ letterSpacing: "-0.03em" }}>
              Choose a <span style={{ color: C.teal }}>fresh password</span>
            </h1>
            <p className="mt-2 text-sm text-white/60">
              Once saved, you&apos;ll be logged in on this device. Other sessions are signed out for safety.
            </p>
          </div>

          <div className="rounded-2xl p-7 border" style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.15)" }}>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">New password</label>
                <input
                  type="password"
                  value={pw}
                  onChange={(e) => setPw(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  data-testid="reset-password-input"
                  placeholder="At least 6 characters"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white placeholder:text-white/30"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Confirm password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-password-confirm-input"
                  placeholder="Type it again"
                  className="mt-1.5 w-full px-3 py-2.5 rounded-md focus:outline-none focus:border-[#0A8B85] text-sm text-white placeholder:text-white/30"
                  style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="reset-password-submit"
                className="w-full py-2.5 rounded-md font-semibold text-white transition-colors disabled:opacity-60"
                style={{ backgroundColor: busy ? C.tealHover : C.teal }}
              >
                {busy ? "Updating…" : "Set new password"}
              </button>
            </form>
          </div>

          <div className="mt-6 text-center text-sm text-white/60">
            Link expired?{" "}
            <Link to="/forgot-password" data-testid="reset-to-forgot" className="font-semibold hover:underline" style={{ color: C.tealSoft }}>
              Request a fresh one
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
